import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../supabase';
import SignatureCanvas from 'react-signature-canvas';
import { PDFDocument } from 'pdf-lib';

function FirmaRecibo({ recibo, onCerrar, onFirmado }) {
  const [paso, setPaso] = useState(1);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [firmaExistente, setFirmaExistente] = useState(null);
  const [mostrarCanvas, setMostrarCanvas] = useState(false);
  const sigCanvas = useRef(null);

  const btnStyle = { background: '#7c6ff7', color: 'white', border: 'none', padding: '8px 20px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 };

  useEffect(() => { cargarFirma(); }, []);

  async function cargarFirma() {
    const { data: { user } } = await supabase.auth.getUser();
    const { data } = await supabase.from('perfiles').select('firma_url').eq('id', user.id).single();
    if (data?.firma_url) setFirmaExistente(data.firma_url);
  }

  async function guardarFirmaPersonal() {
    if (sigCanvas.current.isEmpty()) { alert('Dibuja tu firma antes de continuar'); return; }
    setCargando(true);
    const { data: { user } } = await supabase.auth.getUser();
    const dataUrl = sigCanvas.current.getCanvas().toDataURL('image/png');
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const nombre = `firma_${user.id}.png`;
    await supabase.storage.from('fotos').upload(nombre, blob, { upsert: true, contentType: 'image/png' });
    const { data: urlData } = supabase.storage.from('fotos').getPublicUrl(nombre);
    await supabase.from('perfiles').update({ firma_url: urlData.publicUrl }).eq('id', user.id);
    setFirmaExistente(urlData.publicUrl);
    setMostrarCanvas(false);
    setCargando(false);
  }

  async function insertarFirmaEnPDF(firmaUrl, reciboUrl, empleadoNombre, periodo) {
    try {
      // Descargar el PDF original
      const pdfRes = await fetch(reciboUrl);
      const pdfBytes = await pdfRes.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      // Descargar la imagen de la firma
      const firmaRes = await fetch(firmaUrl);
      const firmaBytes = await firmaRes.arrayBuffer();
      const firmaImg = await pdfDoc.embedPng(firmaBytes);

      // Ir a la ultima pagina
      const paginas = pdfDoc.getPages();
      const ultimaPagina = paginas[paginas.length - 1];
      const { width, height } = ultimaPagina.getSize();

      // Insertar firma abajo a la derecha
      const firmaWidth = 150;
      const firmaHeight = 60;
      ultimaPagina.drawImage(firmaImg, {
        x: width - firmaWidth - 40,
        y: 60,
        width: firmaWidth,
        height: firmaHeight,
        opacity: 0.9,
      });

      // Agregar texto debajo de la firma
      const { rgb } = await import('pdf-lib');
      ultimaPagina.drawText(`Firmado por: ${empleadoNombre}`, {
        x: width - firmaWidth - 40,
        y: 48,
        size: 7,
        color: rgb(0.4, 0.4, 0.4),
      });
      ultimaPagina.drawText(`Periodo: ${periodo} | ${new Date().toLocaleDateString('es-AR')}`, {
        x: width - firmaWidth - 40,
        y: 38,
        size: 6,
        color: rgb(0.4, 0.4, 0.4),
      });

      // Guardar PDF firmado
      const pdfFirmadoBytes = await pdfDoc.save();
      const blob = new Blob([pdfFirmadoBytes], { type: 'application/pdf' });

      // Subir PDF firmado
      const { data: { user } } = await supabase.auth.getUser();
      const nombreFirmado = `recibo_firmado_${recibo.id}_${user.id}.pdf`;
      await supabase.storage.from('fotos').upload(nombreFirmado, blob, { upsert: true, contentType: 'application/pdf' });
      const { data: urlData } = supabase.storage.from('fotos').getPublicUrl(nombreFirmado);

      // Actualizar URL del recibo con el PDF firmado
      await supabase.from('recibos').update({ url: urlData.publicUrl }).eq('id', recibo.id);

      return true;
    } catch (e) {
      console.error('Error insertando firma en PDF:', e);
      return false;
    }
  }

  async function confirmarFirma() {
    if (!firmaExistente) { setError('Debes crear tu firma primero'); return; }
    if (!password) { setError('Ingresa tu contrasena'); return; }
    setCargando(true);
    setError('');

    const { data: { user } } = await supabase.auth.getUser();
    const { error: authError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: password
    });

    if (authError) { setError('Contrasena incorrecta'); setCargando(false); return; }

    let ip = 'No disponible';
    try {
      const res = await fetch('https://api.ipify.org?format=json');
      const data = await res.json();
      ip = data.ip;
    } catch {}

    const { data: perfil } = await supabase.from('perfiles').select('nombre').eq('id', user.id).single();

    // Insertar firma en PDF
    await insertarFirmaEnPDF(firmaExistente, recibo.url, perfil?.nombre || '', recibo.periodo);

    const ahora = new Date().toISOString();

    await supabase.from('firmas_recibos').insert([{
      recibo_id: recibo.id,
      empleado_id: user.id,
      ip_address: ip,
      user_agent: navigator.userAgent,
      confirmado: true,
    }]);

    await supabase.from('recibos').update({
      firmado: true,
      fecha_firma: ahora
    }).eq('id', recibo.id);

    setCargando(false);
    setPaso(3);
    onFirmado();
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 600 }}>

        {paso === 1 && (
          <div>
            <h2>Ver recibo antes de firmar</h2>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: 16 }}>
              Debes visualizar el recibo antes de poder firmarlo.
            </p>
            <div style={{ background: '#f8f8f8', border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>{recibo.periodo}</div>
              <div style={{ fontSize: '0.85rem', color: '#888' }}>
                Subido el {new Date(recibo.created_at).toLocaleDateString('es-AR')}
              </div>
            </div>
            <button style={{ background: '#1a1a2e', color: 'white', border: 'none', padding: '10px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, width: '100%', marginBottom: 16 }}
              onClick={() => window.open(recibo.url, '_blank')}>
              Ver PDF del recibo
            </button>
            <div className="modal-botones">
              <button type="button" onClick={onCerrar}>Cancelar</button>
              <button type="button" style={btnStyle} onClick={() => setPaso(2)}>
                Ya lo visualice - Firmar
              </button>
            </div>
          </div>
        )}

        {paso === 2 && (
          <div>
            <h2>Confirmar firma</h2>
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 8, fontSize: '0.9rem' }}>Tu firma personal</p>
              {firmaExistente && !mostrarCanvas ? (
                <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, background: '#f8f8f8', textAlign: 'center' }}>
                  <img src={firmaExistente} alt="Tu firma" style={{ maxHeight: 80, maxWidth: '100%' }} />
                  <div style={{ marginTop: 8 }}>
                    <button onClick={() => setMostrarCanvas(true)} style={{ background: 'none', border: 'none', color: '#7c6ff7', cursor: 'pointer', fontSize: '0.82rem', textDecoration: 'underline' }}>
                      Cambiar firma
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {!firmaExistente && (
                    <div style={{ background: '#fff8e1', border: '1px solid #f59e0b', borderRadius: 8, padding: 10, marginBottom: 10, fontSize: '0.82rem', color: '#92400e' }}>
                      No tenes firma registrada. Dibuja tu firma para continuar.
                    </div>
                  )}
                  <div style={{ border: '2px solid #7c6ff7', borderRadius: 8, overflow: 'hidden', background: 'white' }}>
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{ width: 500, height: 150, style: { width: '100%', height: 150 } }}
                      backgroundColor="white"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button onClick={() => sigCanvas.current.clear()} style={{ flex: 1, padding: '6px', border: '1px solid #ddd', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: '0.85rem' }}>
                      Borrar
                    </button>
                    <button onClick={guardarFirmaPersonal} disabled={cargando} style={{ flex: 2, ...btnStyle, padding: '6px 12px' }}>
                      {cargando ? 'Guardando...' : 'Guardar firma'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {firmaExistente && (
              <div>
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: 8 }}>
                  Ingresa tu contrasena para confirmar la firma del recibo <strong>{recibo.periodo}</strong>.
                </p>
                <div style={{ background: '#fff8e1', border: '1px solid #f59e0b', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: '0.82rem', color: '#92400e' }}>
                  Al firmar confirmas que recibiste y leiste este recibo. Validez legal Ley 25.506.
                </div>
                <input
                  type="password"
                  placeholder="Tu contrasena"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && confirmarFirma()}
                  style={{ width: '100%', padding: 12, border: '1px solid #ddd', borderRadius: 8, fontSize: '1rem', marginBottom: 8 }}
                />
                {error && <p style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: 8 }}>{error}</p>}
              </div>
            )}

            <div className="modal-botones">
              <button type="button" onClick={() => setPaso(1)}>Volver</button>
              {firmaExistente && (
                <button type="button" onClick={confirmarFirma} disabled={cargando} style={btnStyle}>
                  {cargando ? 'Firmando...' : 'Confirmar firma'}
                </button>
              )}
            </div>
          </div>
        )}

        {paso === 3 && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>✅</div>
            <h2 style={{ color: '#22c55e', marginBottom: 8 }}>Recibo firmado correctamente</h2>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              Tu firma quedo registrada el {new Date().toLocaleDateString('es-AR')} a las {new Date().toLocaleTimeString('es-AR')}.
            </p>
            <div className="modal-botones" style={{ justifyContent: 'center', marginTop: 20 }}>
              <button type="button" onClick={onCerrar} style={btnStyle}>Cerrar</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default FirmaRecibo;